// Pasted pictures that live in Cloud Storage instead of inside the email body.
//
// A pasted screenshot used to be embedded as a base64 `data:` URL inside the draft's HTML. A draft is ONE
// Firestore document (1 MiB cap), so two or three screenshots made it impossible to save. Now the picture is
// compressed, uploaded to mail_attachments/{uid}/{draftKey}/... (the same place and rules as file
// attachments) and the body only holds an ordinary <img src="<download url>">: a few hundred bytes.
// The server turns those tags into inline (cid:) attachments when the mail is sent
// (functions/src/services/mail/inlineImages.ts - keep the URL rule here identical to that file).
//
// Pure (no Firebase / network), so it can be unit-tested.

const STORAGE_ROOT = 'mail_attachments';
const DOWNLOAD_URL_RE = /^https:\/\/firebasestorage\.googleapis\.com\/v0\/b\/([^/?#]+)\/o\/([^?#]+)/;
const IMG_TAG_RE = /<img\b(?:[^>"']|"[^"]*"|'[^']*')*>/gi;
const SRC_RE = /\ssrc\s*=\s*(?:"([^"]*)"|'([^']*)')/i;

/** Same rule as attachmentStorage.isMailAttachmentPath and the server's isValidStoragePath. */
function isMailPath(p: string): boolean {
  if (!p.startsWith(`${STORAGE_ROOT}/`) || p.length <= STORAGE_ROOT.length + 1) return false;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f\\]/.test(p)) return false;
  return p.split('/').every((seg) => {
    if (seg === '' || seg === '.' || seg === '..') return false;
    try {
      const dec = decodeURIComponent(seg);
      return dec !== '.' && dec !== '..';
    } catch {
      return true;
    }
  });
}

/**
 * The mail_attachments/ object a download URL points at, or '' for anything else (another host / bucket /
 * folder, a traversal attempt, a data: URL). `bucket` empty = any bucket (only used to protect objects).
 */
export function storagePathFromImageSrc(src: string, bucket?: string): string {
  const m = DOWNLOAD_URL_RE.exec(String(src || '').trim().replace(/&amp;/gi, '&'));
  if (!m) return '';
  if (bucket && m[1] !== bucket) return '';
  let path: string;
  try {
    path = decodeURIComponent(m[2]);
  } catch {
    return '';
  }
  return isMailPath(path) ? path : '';
}

/** Every distinct Storage object the body's <img> tags point at, in order. */
export function inlineImagePaths(html: string, bucket?: string): string[] {
  const s = typeof html === 'string' ? html : '';
  if (!s || s.indexOf('mail_attachments') < 0) return [];
  const out: string[] = [];
  for (const tag of s.match(IMG_TAG_RE) || []) {
    const m = SRC_RE.exec(tag);
    const p = m ? storagePathFromImageSrc(m[1] ?? m[2] ?? '', bucket) : '';
    if (p && out.indexOf(p) < 0) out.push(p);
  }
  return out;
}

/**
 * Bytes of the Storage-hosted pictures in the body whose size this composer knows (it recorded the size when
 * it uploaded them). They count toward the 20 MiB total exactly like a pasted `data:` image always did. A
 * picture from a reopened draft is unknown here (0) - the server still enforces the limit on the real size.
 */
export function storageImageBytes(html: string, knownBytes: ReadonlyMap<string, number>, bucket?: string): number {
  let total = 0;
  for (const p of inlineImagePaths(html, bucket)) total += knownBytes.get(p) || 0;
  return total;
}

/** How many pasted pictures are still embedded as base64 `data:` URLs in the body. */
export function dataImageCount(html: string): number {
  return (String(html || '').match(/<img\b[^>]*\bsrc\s*=\s*["']\s*data:image\//gi) || []).length;
}
